/**
 * Position Asset Swap Dialog
 *
 * Self-contained dialog for swapping vault positions from the "My Positions" dropdown.
 * Includes vault selector, amount input, swap quote fetching, and execution.
 * All in one unified view - user can always change the vault.
 */

'use client'

import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { invalidateBalancesCache } from '@/domain/cache'
import { getTokenImageUrl } from '@/domain/euler'
import { getSubAccount } from '@/domain/evc'
import { useEulerSwapQuote } from '@/domain/euler/swap/useEulerSwapQuote'
import { useSimulatedBestQuote } from '@/domain/euler/swap/useSimulatedBestQuote'
import { hasSwapAndVerify } from '@/domain/euler/swap/types'
import type { EulerSwapResponse } from '@/domain/euler/swap/types'
import { SwapErrorDisplay } from '@/features/actions/components/SwapErrorDisplay'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { useWrongNetwork } from '@/domain/hooks/useWrongNetwork'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import type { CommonDialogProps, DialogConfig } from '@/features/dialogs/common/types'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { useELend, type ELendVaultData } from '@/features/elend/logic/useELend'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { addIntrinsicAPY } from '@/domain/euler/shared/apyUtils'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { usePreviewSupplyAPY } from '@/features/elend/logic/usePreviewSupplyAPY'
import { ApyChange } from '@/ui/atoms/apy-change/ApyChange'
import { SlippagePanel } from '@/ui/molecules/slippage-panel/SlippagePanel'
import { isUserRejection } from '@/lib/errors'
import { normalizeForSearch } from '@/utils/strings'
import { useDebounce } from '@/utils/useDebounce'
import {
  type TransactionItems,
  TransactionLabel,
  type VaultSwapTransactionItem,
  type WithdrawTransactionItem,
  type SwapMulticallTransactionItem,
  type SwapVerifierSkimMinTransaction,
  type SkimTransactionItem,
  type EnableCollateralTransactionItem,
  type Asset,
  useSimulateEVCBatchCall,
  SwapperMode,
} from '@eulerxyz/wagmi-evc'
import { useQueryClient } from '@tanstack/react-query'
import { assets } from '@/ui/assets'
import { formatPercentage } from '@/domain/common/format'
import { ArrowDown, ChevronDown, Loader2, Settings } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type Address, formatUnits, parseUnits } from 'viem'
import { useAccount, useConfig, useWriteContract, useReadContract } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { PositionAssetSwapSuccessView } from './components/PositionAssetSwapSuccessView'
import { UndercollateralizedWarning } from './components/UndercollateralizedWarning'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { EVC_ADDRESS } from '@/config/euler-contracts'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { eEarnVaultImplementationAbi } from 'abis/evm/eEarnVaultImplementation'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'

export interface PositionAssetSwapDialogProps extends CommonDialogProps {
  /** Source vault address */
  fromVaultAddress: Address
  /** Source vault symbol (e.g., "eUSDH-2") */
  fromVaultSymbol: string
  /** Source vault asset symbol (e.g., "USDH") */
  fromAssetSymbol: string
  /** Source vault decimals */
  fromDecimals: number
  /** Source underlying asset address */
  fromUnderlyingAsset: Address
  /** User's current balance in the source vault (formatted as string for precision) */
  userBalance: string
  /** User's current balance value in USD */
  userBalanceUsd: number
  /** Sub-account ID */
  subAccountId: number
  /** Logo URI for source token */
  logoURI?: string
}

function PositionAssetSwapDialogContent({
  closeDialog,
  fromVaultAddress,
  fromAssetSymbol,
  fromDecimals,
  fromUnderlyingAsset,
  userBalance,
  userBalanceUsd,
  subAccountId,
  logoURI,
}: Omit<PositionAssetSwapDialogProps, 'open' | 'setOpen'> & { closeDialog: () => void }) {
  const { address: userAddress } = useAccount()
  const config = useConfig()
  const queryClient = useQueryClient()
  const chainId = E_LEND_CHAIN_ID

  // State
  const [amount, setAmount] = useState('')
  const [selectedVault, setSelectedVault] = useState<ELendVaultData | null>(null)
  const successTokensRef = useRef<{ fromToken: Token; toToken: Token; quote: EulerSwapResponse } | null>(null)

  // Debounce logic for amount input
  const [keyCounter, setKeyCounter] = useState(0)
  const prevAmountRef = useRef(amount)

  useEffect(() => {
    if (prevAmountRef.current !== amount) {
      prevAmountRef.current = amount
      setKeyCounter((prev) => prev + 1)
    }
  }, [amount])

  const { debouncedValue: debouncedAmount, isDebouncing } = useDebounce(amount, `swap-amount-${keyCounter}`, {
    delay: 300,
  })
  const [vaultSelectorOpen, setVaultSelectorOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [slippage, setSlippage] = useState<Percentage>(Percentage(0.005)) // 0.5% default

  // Fetch all vaults for destination selection
  const { vaults, isLoading: isVaultsLoading } = useELend(userAddress)

  // Debounced amount in base units (for simulation/API calls)
  const debouncedAmountBaseUnit = useMemo(() => {
    if (!debouncedAmount || Number(debouncedAmount) <= 0) return undefined
    try {
      return parseUnits(debouncedAmount, fromDecimals).toString()
    } catch {
      return undefined
    }
  }, [debouncedAmount, fromDecimals])

  // Build position address
  const position = useMemo(() => {
    if (!userAddress) return undefined
    return getSubAccount(userAddress, subAccountId)
  }, [userAddress, subAccountId])

  // Get collaterals enabled for this position (needed for vault filtering and transaction building)
  const { data: collaterals } = useReadContract({
    address: EVC_ADDRESS,
    abi: eulerVaultConnectorAbi,
    functionName: 'getCollaterals',
    args: position ? [position] : undefined,
    chainId,
    query: {
      enabled: !!position,
    },
  })

  // Get controllers (debt vaults) for this position - needed to exclude from destination options
  const { data: controllers } = useReadContract({
    address: EVC_ADDRESS,
    abi: eulerVaultConnectorAbi,
    functionName: 'getControllers',
    args: position ? [position] : undefined,
    chainId,
    query: {
      enabled: !!position,
    },
  })

  // Check if the source vault is used as collateral for this position
  const sourceVaultIsCollateral = useMemo(() => {
    if (!collaterals) return false
    return collaterals.some((collateral) => collateral.toLowerCase() === fromVaultAddress.toLowerCase())
  }, [collaterals, fromVaultAddress])

  // Filter vaults for destination (exclude source vault and debt vaults) and sort by APY descending
  // For collateral swaps: restrict to same market type (Prime to Prime, Yield to Yield)
  const destinationVaults = useMemo(() => {
    if (!vaults) return []

    // Get source vault's market
    const sourceVault = vaults.find((v) => v.address.toLowerCase() === fromVaultAddress.toLowerCase())
    const sourceMarketKey = sourceVault?.market?.key

    // Normalize controller addresses for comparison
    const controllerAddresses = controllers?.map((c) => c.toLowerCase()) ?? []

    return vaults
      .filter((v) => {
        const vaultAddressLower = v.address.toLowerCase()

        // Always exclude source vault
        if (vaultAddressLower === fromVaultAddress.toLowerCase()) return false

        // Exclude debt vaults (controllers) - can't swap collateral to where you have debt
        if (controllerAddresses.includes(vaultAddressLower)) return false

        // For collateral swaps: only show vaults from the same market
        // If sourceMarketKey is unknown, block all vaults to prevent unsafe swaps
        if (sourceVaultIsCollateral) {
          if (!sourceMarketKey) return false
          return v.market?.key === sourceMarketKey
        }

        return true
      })
      .sort((a, b) => (b.supplyAPY ?? 0) - (a.supplyAPY ?? 0))
  }, [vaults, fromVaultAddress, sourceVaultIsCollateral, controllers])

  // Filter by search
  // Search input is safe — value is only used for client-side .filter(),
  // never rendered as raw HTML. React JSX auto-escapes strings (no XSS risk).
  // Input length is bounded by the UI (single-line text field) and only lives in local state.
  const filteredVaults = useMemo(() => {
    if (!searchQuery.trim()) return destinationVaults
    const query = normalizeForSearch(searchQuery)
    return destinationVaults.filter(
      (v) =>
        normalizeForSearch(v.symbol).includes(query) ||
        normalizeForSearch(v.assetSymbol || '').includes(query) ||
        normalizeForSearch(v.name || '').includes(query) ||
        normalizeForSearch(v.curatorName || '').includes(query) ||
        normalizeForSearch(v.market?.name || '').includes(query),
    )
  }, [destinationVaults, searchQuery])

  // Get source vault info
  const fromVaultInfo = useMemo(() => {
    return vaults?.find((v) => v.address.toLowerCase() === fromVaultAddress.toLowerCase())
  }, [vaults, fromVaultAddress])

  // Check if same underlying asset (no swap needed)
  const isSameUnderlying = useMemo(() => {
    if (!selectedVault || !selectedVault.underlyingAsset) return false
    return fromUnderlyingAsset.toLowerCase() === selectedVault.underlyingAsset.toLowerCase()
  }, [fromUnderlyingAsset, selectedVault])

  // Fetch maxDeposit for the selected destination vault to check supply cap
  const { data: destMaxDepositRaw } = useReadContract({
    address: selectedVault?.address as Address | undefined,
    abi: eEarnVaultImplementationAbi,
    functionName: 'maxDeposit',
    args: userAddress ? [userAddress] : ['0x0000000000000000000000000000000000000000'],
    chainId,
    query: {
      enabled: !!selectedVault?.address,
    },
  })

  // Fetch swap quote (for both different and same underlying assets)
  // Backend supports 1:1 pass-through swaps for same-underlying assets
  // Uses debounced amount to avoid excessive API calls
  const {
    quote: swapQuote,
    quotes: allQuotes,
    isLoading: isQuoteLoading,
    isRefetching: isQuoteRefetching,
    error: quoteError,
    refetch: refetchQuotes,
  } = useEulerSwapQuote({
    tokenIn: fromUnderlyingAsset,
    tokenOut: selectedVault?.underlyingAsset as Address | undefined,
    amount: debouncedAmountBaseUnit,
    vaultIn: fromVaultAddress,
    receiver: selectedVault?.address as Address | undefined,
    accountIn: position,
    accountOut: position,
    slippage: Number(slippage.toFixed(4)) * 100, // Convert Percentage to number (0.005 -> 0.5)
    enabled: !!debouncedAmountBaseUnit && !!selectedVault && !!position && Number(debouncedAmount) > 0,
    skipSweepDepositOut: false,
  })

  // Handle max button
  const handleMax = useCallback(() => {
    setAmount(userBalance)
  }, [userBalance])

  // Compute supply cap error for destination vault
  const destSupplyCapError = useMemo(() => {
    if (!selectedVault || destMaxDepositRaw === undefined) return null

    const destDecimals = selectedVault.underlyingTokenInfo?.decimals ?? selectedVault.decimals ?? 18
    const maxDeposit = NormalizedUnitNumber(formatUnits(destMaxDepositRaw, destDecimals))

    if (maxDeposit.isZero()) {
      return 'Supply cap has been reached on the destination vault. No additional deposits are allowed.'
    }

    if (swapQuote) {
      const outAmount = NormalizedUnitNumber(formatUnits(BigInt(swapQuote.amountOut), swapQuote.tokenOut.decimals))
      if (outAmount.gt(maxDeposit)) {
        const symbol = selectedVault.assetSymbol || selectedVault.symbol
        return `This swap would exceed the destination vault's supply cap. Maximum deposit: ${maxDeposit.toFixed(2)} ${symbol}`
      }
    }

    return null
  }, [selectedVault, destMaxDepositRaw, swapQuote])

  // Preview supply APY for destination vault after deposit
  const toDepositBigInt = useMemo(() => {
    if (!swapQuote || !selectedVault) return 0n
    try {
      return BigInt(swapQuote.amountOut)
    } catch {
      return 0n
    }
  }, [swapQuote, selectedVault])

  const { newAPY: toPreviewNewAPY, isLoading: isToPreviewLoading } = usePreviewSupplyAPY({
    vaultAddress: selectedVault?.address as Address,
    changeAmount: toDepositBigInt,
    changeType: 'deposit',
    chainId: E_LEND_CHAIN_ID,
    enabled: !!selectedVault && toDepositBigInt > 0n,
  })

  // Handle vault selection
  const handleSelectVault = useCallback((vault: ELendVaultData) => {
    setSelectedVault(vault)
    setVaultSelectorOpen(false)
    setSearchQuery('')
  }, [])

  // Build from token for display
  const fromToken = useMemo(() => {
    const balanceNum = Number(userBalance)
    return new Token({
      address: CheckedAddress(fromUnderlyingAsset),
      symbol: TokenSymbol(fromAssetSymbol),
      name: fromAssetSymbol,
      decimals: fromDecimals,
      unitPriceUsd: userBalanceUsd > 0 && balanceNum > 0 ? (userBalanceUsd / balanceNum).toString() : '0',
      logoURI,
    })
  }, [fromUnderlyingAsset, fromAssetSymbol, fromDecimals, userBalanceUsd, userBalance, logoURI])

  // Can execute swap?
  const canExecute = useMemo(() => {
    const amountNum = Number(amount)
    if (!selectedVault || amountNum <= 0 || amountNum > Number(userBalance)) return false
    if (destSupplyCapError) return false
    // Need swap quote for both same-underlying and different-underlying
    // Backend supports 1:1 pass-through swaps for same-underlying assets
    return !!swapQuote
  }, [selectedVault, amount, userBalance, swapQuote, destSupplyCapError])

  // Build transactions for a given quote (used by useSimulatedBestQuote to simulate all quotes)
  const buildTransactions = useCallback(
    (quote: EulerSwapResponse): TransactionItems => {
      // Basic requirements
      if (!userAddress || !position || !selectedVault) return []
      if (Number(debouncedAmount) <= 0 || !debouncedAmountBaseUnit) return []

      // For different-underlying swaps, we need swap and verify data
      if (!isSameUnderlying) {
        if (!hasSwapAndVerify(quote)) return []
      }

      // Build asset objects from swap quote
      const fromAsset: Asset = {
        address: quote.tokenIn.address,
        name: quote.tokenIn.name,
        symbol: quote.tokenIn.symbol,
        decimals: quote.tokenIn.decimals,
        logoURI: quote.tokenIn.logoURI,
      }

      const toAsset: Asset = {
        address: quote.tokenOut.address,
        name: quote.tokenOut.name,
        symbol: quote.tokenOut.symbol,
        decimals: quote.tokenOut.decimals,
        logoURI: quote.tokenOut.logoURI,
      }

      // Build sub-transactions based on whether it's same-underlying or different-underlying
      // This matches the pattern in euler-protocol-monorepo selectors
      type SubTransaction =
        | WithdrawTransactionItem
        | SkimTransactionItem
        | SwapMulticallTransactionItem
        | SwapVerifierSkimMinTransaction
        | EnableCollateralTransactionItem

      const subTransactions: SubTransaction[] = []

      if (isSameUnderlying) {
        // Same underlying: Withdraw to destination vault + Skim
        // Pattern from swapSameAsset selector
        const withdrawTx: WithdrawTransactionItem = {
          label: TransactionLabel.WITHDRAW,
          from: position,
          to: selectedVault.address as Address, // withdraw TO destination vault
          vault: fromVaultAddress,
          asset: fromAsset,
          value: quote.amountIn,
          isMax: false,
          userShares: '0', // Not used when isMax is false
        }

        const skimTx: SkimTransactionItem = {
          label: TransactionLabel.SKIM,
          from: position,
          to: position,
          vault: selectedVault.address as Address,
          asset: toAsset,
          value: quote.amountIn,
          isMax: false,
        }

        subTransactions.push(withdrawTx, skimTx)
      } else {
        // Different underlying: Withdraw to swapper + SwapMulticall + SwapVerifier
        // Pattern from swapDifferentAsset selector
        // swap and verify are guaranteed non-null here (checked above for !isSameUnderlying)
        const swap = quote.swap!
        const verify = quote.verify!

        const withdrawTx: WithdrawTransactionItem = {
          label: TransactionLabel.WITHDRAW,
          from: position,
          to: swap.swapperAddress as Address, // withdraw TO swapper address
          vault: fromVaultAddress,
          asset: fromAsset,
          value: quote.amountIn,
          isMax: false,
          userShares: '0', // Not used when isMax is false
        }

        // Build SwapMulticallTransactionItem - matches getSwapAndVerifyTransactions in euler-protocol-monorepo
        const swapMulticallTx: SwapMulticallTransactionItem = {
          label: TransactionLabel.SWAP_MULTICALL,
          from: quote.accountIn as Address,
          to: quote.accountOut as Address,
          asset: fromAsset,
          fromVault: quote.vaultIn as Address,
          toVault: quote.receiver as Address,
          accountTo: quote.accountOut as Address,
          receiver: quote.receiver as Address,
          amountIn: quote.amountIn,
          assetIn: fromAsset,
          amountOut: quote.amountOut,
          assetOut: toAsset,
          calls: swap.multicallItems.map((m) => m.data),
          swapperAddress: swap.swapperAddress as Address,
          swapperData: swap.swapperData,
        }

        // Build SwapVerifierTransaction - matches getSwapAndVerifyTransactions in euler-protocol-monorepo
        const swapVerifierTx: SwapVerifierSkimMinTransaction = {
          label: TransactionLabel.SWAP_VERIFIER_SKIM_MIN,
          to: quote.accountOut as Address,
          from: quote.accountOut as Address,
          asset: toAsset,
          vault: verify.vault as Address,
          account: verify.account as Address,
          amount: verify.amount,
          deadline: String(verify.deadline),
          verifierAddress: verify.verifierAddress as Address,
          verifierData: verify.verifierData,
        }

        subTransactions.push(withdrawTx, swapMulticallTx, swapVerifierTx)
      }

      // If swapping FROM collateral, ALWAYS enable destination as collateral
      // This prevents users from accidentally converting collateral to non-collateral
      // and getting liquidated
      if (sourceVaultIsCollateral) {
        const enableCollateralTx: EnableCollateralTransactionItem = {
          label: TransactionLabel.ENABLE_COLLATERAL,
          from: position,
          to: position,
          vault: selectedVault.address as Address,
          asset: toAsset,
        }
        subTransactions.push(enableCollateralTx)
      }

      // Use VaultSwapTransactionItem as the compound transaction wrapper
      const vaultSwapTx: VaultSwapTransactionItem = {
        label: TransactionLabel.VAULT_SWAP,
        from: position,
        to: position,
        transactions: subTransactions,
        fromVault: fromVaultAddress,
        toVault: selectedVault.address as Address,
        swapperMode: SwapperMode.EXACT_IN,
        fromAmount: formatUnits(BigInt(quote.amountIn), quote.tokenIn.decimals),
        toAmount: formatUnits(BigInt(quote.amountOutMin), quote.tokenOut.decimals),
        isMax: false,
        isSavings: false,
      }

      return [vaultSwapTx]
    },
    [
      userAddress,
      position,
      selectedVault,
      debouncedAmount,
      debouncedAmountBaseUnit,
      fromVaultAddress,
      isSameUnderlying,
      sourceVaultIsCollateral,
    ],
  )

  // Simulate all quotes in parallel and pick the first one that passes.
  // Mirrors the euler-protocol-monorepo approach.
  const {
    quote: validatedQuote,
    transactions: validatedTransactions,
    isSimulating: isValidatingQuotes,
    error: validationError,
    refetch: refetchSimulation,
  } = useSimulatedBestQuote({
    quotes: allQuotes,
    buildTransactions,
    queryKeyDeps: [position, selectedVault?.address, debouncedAmountBaseUnit, fromVaultAddress],
    enabled: allQuotes.length > 0 && canExecute,
  })

  const handleRefetchQuotes = useCallback(() => {
    refetchQuotes()
    refetchSimulation()
  }, [refetchQuotes, refetchSimulation])

  // Get the execution request from the validated quote's transactions.
  // Since the quote already passed simulation(), this should always succeed.
  const {
    data: simulationData,
    isLoading: isSimulating,
    error: simulationError,
  } = useSimulateEVCBatchCall({
    config,
    transactions: validatedTransactions,
    query: {
      enabled: !!userAddress && validatedTransactions.length > 0 && !!validatedQuote,
      retry: false,
    },
  })

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })

  // Execute the batch
  const displayQuote = validatedQuote ?? (validationError ? null : swapQuote)

  const handleExecute = useCallback(() => {
    if (simulationData?.request && displayQuote) {
      const toToken = selectedVault
        ? new Token({
            address: CheckedAddress(selectedVault.underlyingAsset ?? selectedVault.address),
            symbol: TokenSymbol(selectedVault.assetSymbol || selectedVault.symbol),
            name: selectedVault.name,
            decimals: selectedVault.decimals,
            unitPriceUsd: selectedVault.assetPrice?.toString() ?? '0',
            logoURI: getTokenImageUrl(
              selectedVault.underlyingTokenInfo,
              selectedVault.assetSymbol || selectedVault.symbol,
            ),
          })
        : fromToken
      successTokensRef.current = { fromToken, toToken, quote: displayQuote }
      writeContract(simulationData.request as any)
    }
  }, [simulationData, writeContract, fromToken, selectedVault, displayQuote])

  // Determine action handler state
  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    if (isWriting || isWaiting) return 'loading'
    // Show loading during debouncing, quote fetching, quote validation, or execution simulation
    if (isDebouncing || isQuoteLoading || isValidatingQuotes || isSimulating) return 'loading'
    if (!simulationData) {
      return 'disabled'
    }
    return 'ready'
  }, [simulationData, isDebouncing, isQuoteLoading, isValidatingQuotes, isSimulating, isWriting, isWaiting])

  // Filter out user rejection errors
  const displayError = useMemo(() => {
    // Don't show other errors when supply cap is the root cause
    if (destSupplyCapError) return null
    const errors = [quoteError, validationError, simulationError, writeError, waitError].filter(Boolean)
    // Find first error that isn't a user rejection
    return errors.find((err) => !isUserRejection(err as Error)) || null
  }, [quoteError, validationError, simulationError, writeError, waitError, destSupplyCapError])

  const swapTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    swapTimeout.handleActionClick(handleExecute)
  }, [swapTimeout, handleExecute])

  // Mark as success when confirmed
  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)
      invalidateBalancesCache({ queryClient, chainId })
    }
  }, [isConfirmed, isSuccess, queryClient, chainId])

  // Success view — use snapshotted data to survive cache invalidation
  const successSnapshot = successTokensRef.current
  if (isSuccess && successSnapshot) {
    const successFromAmount = NormalizedUnitNumber(
      formatUnits(BigInt(successSnapshot.quote.amountIn), successSnapshot.quote.tokenIn.decimals),
    )
    const successToAmount = NormalizedUnitNumber(
      formatUnits(BigInt(successSnapshot.quote.amountOut), successSnapshot.quote.tokenOut.decimals),
    )

    return (
      <MultiPanelDialog>
        <DialogTitle>{isSameUnderlying ? 'Move Complete' : 'Swap Complete'}</DialogTitle>
        <DialogPanel>
          <PositionAssetSwapSuccessView
            fromToken={successSnapshot.fromToken}
            fromAmount={successFromAmount}
            toToken={successSnapshot.toToken}
            toAmount={successToAmount}
            fromCuratorName={fromVaultInfo?.curatorName}
            fromMarket={fromVaultInfo?.market}
            toCuratorName={selectedVault?.curatorName}
            toMarket={selectedVault?.market}
            onProceed={closeDialog}
            proceedText="Done"
            isSameUnderlying={isSameUnderlying}
          />
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  return (
    <MultiPanelDialog>
      <DialogTitle>Asset Swap</DialogTitle>

      <DialogPanel>
        <div className="flex flex-col gap-4">
          {/* From Section */}
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
            <div className="mb-2">
              <span className="text-muted-foreground text-xs">From</span>
            </div>
            <div className="flex items-center justify-between">
              {/* Left: Token info */}
              <div className="flex items-center gap-3">
                <TokenIcon token={fromToken} className="h-8 w-8" />
                <div className="flex flex-col">
                  <span className="font-medium text-sm text-white">{fromAssetSymbol}</span>
                  {(fromVaultInfo?.curatorName || fromVaultInfo?.market) && (
                    <span className="text-muted-foreground text-xs">
                      {[fromVaultInfo.curatorName && `by ${fromVaultInfo.curatorName}`, fromVaultInfo.market?.name]
                        .filter(Boolean)
                        .join(' • ')}
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Amount input */}
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/,/g, '.').replace(/\s/g, '')
                      if (value === '' || /^\d*\.?\d*$/.test(value)) {
                        setAmount(value)
                      }
                    }}
                    placeholder="0.00"
                    className="w-28 bg-transparent text-right font-semibold text-lg text-white outline-none placeholder:text-muted-foreground"
                  />
                  <button
                    type="button"
                    onClick={handleMax}
                    className="rounded-lg bg-white/5 px-2 py-0.5 text-muted-foreground text-xs hover:bg-white/10"
                  >
                    MAX
                  </button>
                </div>
                <span className="text-muted-foreground text-xs">
                  Balance: {Number(userBalance).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                </span>
              </div>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex justify-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
              <ArrowDown className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          {/* To Section with Vault Selector — entire card is clickable */}
          <div className="overflow-hidden rounded-xl border border-emerald-500/10 bg-emerald-500/[0.02]">
            <Dialog open={vaultSelectorOpen} onOpenChange={setVaultSelectorOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="flex w-full cursor-pointer items-center justify-between rounded-xl p-4 transition-colors hover:bg-emerald-500/[0.04]"
                >
                  {/* Left: Vault info */}
                  <div className="flex items-center gap-3">
                    {selectedVault ? (
                      <>
                        <Image
                          src={getTokenImageUrl(
                            selectedVault.underlyingTokenInfo,
                            selectedVault.assetSymbol || 'UNKNOWN',
                          )}
                          alt={selectedVault.assetSymbol || selectedVault.symbol}
                          width={32}
                          height={32}
                          className="rounded-full"
                        />
                        <div className="flex flex-col items-start">
                          <span className="text-muted-foreground text-xs">Swap to</span>
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-sm text-white">
                              {selectedVault.assetSymbol || selectedVault.symbol}
                            </span>
                          </div>
                          {(selectedVault.curatorName || selectedVault.market) && (
                            <span className="text-muted-foreground text-xs">
                              {[
                                selectedVault.curatorName && `by ${selectedVault.curatorName}`,
                                selectedVault.market?.name,
                              ]
                                .filter(Boolean)
                                .join(' • ')}
                            </span>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/5">
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="flex flex-col items-start">
                          <span className="text-muted-foreground text-xs">Swap to</span>
                          <span className="text-muted-foreground text-sm">Select vault</span>
                        </div>
                      </>
                    )}
                  </div>
                  {/* Right: Output amount + chevron */}
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      {isSameUnderlying ? (
                        <span className="font-semibold text-lg text-white">{Number(amount || 0).toFixed(4)}</span>
                      ) : isQuoteLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      ) : displayQuote?.amountOutMin ? (
                        <span className="font-semibold text-emerald-400 text-lg">
                          ~
                          {Number(
                            formatUnits(BigInt(displayQuote.amountOutMin), displayQuote.tokenOut.decimals),
                          ).toFixed(4)}
                        </span>
                      ) : selectedVault ? (
                        <span className="text-muted-foreground">—</span>
                      ) : null}
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 text-white/50" />
                  </div>
                </button>
              </DialogTrigger>

              <DialogContent
                className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background px-6 pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
                aria-describedby={undefined}
                contentVerticalPosition="top"
                overlayClassName="z-[240]"
                closeClassName="top-6"
              >
                <div className="mb-4">
                  <DialogTitle className="mb-2">
                    <Typography variant="p" className="font-bold text-lg" gradient>
                      Select vault
                    </Typography>
                  </DialogTitle>
                  <SearchInput
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search vaults..."
                    autoFocus
                  />
                </div>

                <div className="max-h-80 space-y-2 overflow-y-auto pb-4">
                  <Typography variant="p" className="pt-3 font-semibold">
                    Available Vaults
                  </Typography>

                  {isVaultsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredVaults.length === 0 ? (
                    <div className="p-4 text-center">
                      <Typography variant="prompt">No vaults found</Typography>
                    </div>
                  ) : (
                    filteredVaults.map((vault) => (
                      <button
                        key={vault.address}
                        type="button"
                        className="flex w-full items-center rounded-sm p-3 transition-colors hover:bg-muted"
                        onClick={() => handleSelectVault(vault)}
                      >
                        <div className="flex w-full flex-row items-center justify-between">
                          <div className="flex flex-row items-center gap-3">
                            <Image
                              src={getTokenImageUrl(vault.underlyingTokenInfo, vault.assetSymbol || 'UNKNOWN')}
                              alt={vault.assetSymbol || vault.symbol}
                              width={24}
                              height={24}
                              className="rounded-full"
                            />
                            <div className="flex flex-col text-left">
                              <Typography className="font-semibold">{vault.assetSymbol || vault.symbol}</Typography>
                              {(vault.curatorName || vault.market) && (
                                <Typography variant="prompt">
                                  {[vault.curatorName && `by ${vault.curatorName}`, vault.market?.name]
                                    .filter(Boolean)
                                    .join(' • ')}
                                </Typography>
                              )}
                            </div>
                          </div>
                          <Typography variant="prompt" className="text-green-400">
                            {(vault.supplyAPY ?? 0).toFixed(2)}% APY
                          </Typography>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Transaction Details */}
          {selectedVault && (
            <div className="mt-4 flex flex-col gap-2">
              <TransactionOverviewDetailsItem label="Current APY">
                {fromVaultInfo?.supplyAPY !== undefined && fromVaultInfo?.lendingAPY !== undefined ? (
                  <SupplyApyTooltip
                    breakdown={{
                      lendingApy: fromVaultInfo.lendingAPY,
                      intrinsicApy: fromVaultInfo.intrinsicAPY,
                      totalApy: fromVaultInfo.supplyAPY,
                    }}
                  >
                    <span className="font-medium text-green-400">{fromVaultInfo.supplyAPY.toFixed(2)}%</span>
                  </SupplyApyTooltip>
                ) : (
                  <span className="text-green-500">0.00%</span>
                )}
              </TransactionOverviewDetailsItem>

              <TransactionOverviewDetailsItem label="New APY">
                <span className="inline-flex items-center gap-1">
                  {selectedVault.supplyAPY !== undefined && selectedVault.lendingAPY !== undefined ? (
                    <SupplyApyTooltip
                      breakdown={{
                        lendingApy: selectedVault.lendingAPY,
                        intrinsicApy: selectedVault.intrinsicAPY,
                        totalApy: selectedVault.supplyAPY,
                      }}
                    >
                      <span className="font-medium text-green-400">{selectedVault.supplyAPY.toFixed(2)}%</span>
                    </SupplyApyTooltip>
                  ) : (
                    <span className="text-green-500">0.00%</span>
                  )}
                  {!isToPreviewLoading && toPreviewNewAPY !== undefined && selectedVault.supplyAPY !== undefined && (
                    <ApyChange
                      currentAPY={selectedVault.supplyAPY}
                      newAPY={addIntrinsicAPY(toPreviewNewAPY, selectedVault.intrinsicAPY)}
                      type="earning"
                    />
                  )}
                </span>
              </TransactionOverviewDetailsItem>

              <TransactionOverviewDetailsItem label="Slippage tolerance">
                {isSameUnderlying ? (
                  <span>N/A (Direct transfer)</span>
                ) : (
                  <SlippagePanel
                    slippage={slippage}
                    setSlippage={setSlippage}
                    title="Swap Slippage"
                    trigger={
                      <button
                        type="button"
                        className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-white"
                      >
                        <span>{formatPercentage(slippage, { minimumFractionDigits: 1 })}</span>
                        <Settings className="h-3.5 w-3.5" />
                      </button>
                    }
                  />
                )}
              </TransactionOverviewDetailsItem>

              {/* Swap via — matches multiply dialog style */}
              <TransactionOverviewDetailsItem label="Swap via">
                {isSameUnderlying ? (
                  <span className="text-emerald-400">Direct (1:1)</span>
                ) : isQuoteLoading || isValidatingQuotes ? (
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                ) : displayQuote ? (
                  <span className="text-green-400">
                    EulerSwap
                    {displayQuote.route?.length
                      ? ` (${displayQuote.route.map((r) => r.providerName).join(' → ')})`
                      : ''}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TransactionOverviewDetailsItem>
            </div>
          )}

          {/* Undercollateralized warning (E_AccountLiquidity) */}
          {displayError &&
            !(Number(amount) > Number(userBalance) && Number(amount) > 0) &&
            ((displayError as Error).message?.includes('34373fbc') ||
              (displayError as Error).message?.includes('E_AccountLiquidity')) && <UndercollateralizedWarning />}

          {/* Supply cap error */}
          {destSupplyCapError && (
            <div className="mt-1 rounded-lg border border-red-500/20 bg-red-500/10 p-3">
              <p className="text-red-400 text-sm">{destSupplyCapError}</p>
            </div>
          )}

          {/* Swap error display (quote-not-found as yellow, other errors as red) */}
          {!(Number(amount) > Number(userBalance) && Number(amount) > 0) && !swapTimeout.isTimedOut && (
            <SwapErrorDisplay
              error={displayError as Error | null}
              onRefetch={handleRefetchQuotes}
              isRefetching={isQuoteRefetching || isValidatingQuotes}
              suppressAccountLiquidity
              className="mt-1"
            />
          )}

          <TimeoutErrorDisplay
            isTimedOut={swapTimeout.isTimedOut}
            onRetry={handleExecuteWithTimeout}
            isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
          />

          {/* Insufficient balance warning */}
          {Number(amount) > Number(userBalance) && Number(amount) > 0 && (
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
              <p className="text-sm text-yellow-400">
                Insufficient balance. You have{' '}
                {Number(userBalance).toLocaleString(undefined, { maximumFractionDigits: 4 })} {fromAssetSymbol}.
              </p>
            </div>
          )}

          <BigBlocksBanner className="mt-4" />

          {/* Execute Button */}
          <div className="mt-4 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
            <ActionRow index={1}>
              <ActionRow.Icon
                path={assets.actions.exchange}
                actionStatus={
                  isDebouncing || isQuoteLoading || isSimulating ? 'loading' : canExecute ? executeStatus : 'disabled'
                }
              />
              <ActionRow.Title
                icon={<TokenIcon token={fromToken} className="h-6" />}
                actionStatus={
                  isDebouncing || isQuoteLoading || isSimulating ? 'loading' : canExecute ? executeStatus : 'disabled'
                }
              >
                Swap Assets
              </ActionRow.Title>
              <ActionRow.Description
                successMessage="Swapped!"
                actionStatus={
                  isDebouncing || isQuoteLoading || isSimulating ? 'loading' : canExecute ? executeStatus : 'disabled'
                }
                variant="compact"
              />
              <ActionRow.Action
                onAction={handleExecuteWithTimeout}
                status={
                  isDebouncing || isQuoteLoading || isSimulating ? 'loading' : canExecute ? executeStatus : 'disabled'
                }
              >
                {isDebouncing || isQuoteLoading || isSimulating ? 'Preparing...' : 'Swap'}
              </ActionRow.Action>
            </ActionRow>
          </div>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}

function PositionAssetSwapDialog({
  open,
  setOpen,
  fromVaultAddress,
  fromVaultSymbol,
  fromAssetSymbol,
  fromDecimals,
  fromUnderlyingAsset,
  userBalance,
  userBalanceUsd,
  subAccountId,
  logoURI,
}: PositionAssetSwapDialogProps) {
  const isWrongNetwork = useWrongNetwork()

  // Close dialog when on wrong network (useEffect to avoid state update during render)
  useEffect(() => {
    if (isWrongNetwork && open) {
      setOpen(false)
    }
  }, [isWrongNetwork, open, setOpen])

  if (isWrongNetwork) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-describedby={undefined}>
        <PositionAssetSwapDialogContent
          fromVaultAddress={fromVaultAddress}
          fromVaultSymbol={fromVaultSymbol}
          fromAssetSymbol={fromAssetSymbol}
          fromDecimals={fromDecimals}
          fromUnderlyingAsset={fromUnderlyingAsset}
          userBalance={userBalance}
          userBalanceUsd={userBalanceUsd}
          subAccountId={subAccountId}
          logoURI={logoURI}
          closeDialog={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

// Dialog config for useOpenDialog
export const positionAssetSwapDialogConfig: DialogConfig<PositionAssetSwapDialogProps> = {
  options: {
    closeOnChainChange: true,
  },
  element: PositionAssetSwapDialog,
}
